import http from 'utils/http';
import fileDownload from 'utils/file-download';
import { File, MoveFilePayload } from '../index';
import uuid from 'uuid';

const CHUNK_SIZE = 1024 * 1000 * 10; // 10MB

function noop() {}

function slice(file: any, start: number, end: number) {
  const slice = file.mozSlice
    ? file.mozSlice
    : file.webkitSlice
    ? file.webkitSlice
    : file.slice
    ? file.slice
    : noop;

  return slice.bind(file)(start, end);
}

async function uploadChunk({
  chunk,
  chunkSize,
  chunkNumber,
  dirId,
  name,
  totalSize,
  uuid,
  onUploadProgress
}: any): Promise<File> {
  const formData = new FormData();
  formData.append('file', chunk);
  formData.append('name', name);
  formData.append('uuid', uuid);
  formData.append('_chunkSize', chunkSize);
  formData.append('_currentChunkSize', chunk.size);
  formData.append('_totalSize', totalSize);
  formData.append('_chunkNumber', chunkNumber);

  if (dirId) {
    formData.append('dir_id', dirId);
  }

  return http.post('api/file/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress
  });
}

function uploadChunked({ file, dirId, onUploadProgress }: any) {
  const size = file.size;
  const name = file.name;
  const uid = uuid();
  const chunkSize = CHUNK_SIZE;
  let start = 0;
  let chunkNumber = 0;

  async function loop(): Promise<File> {
    let end = start + chunkSize;

    if (size - end < 0) {
      end = size;
    }

    const chunk = slice(file, start, end);
    console.log('Preparing chunk', start, end, size, end - start);

    const startProgress = Math.round((start / size) * 100) / 100;
    const endProgress = Math.round((end / size) * 100) / 100;
    const deltaProgress = endProgress - startProgress;

    const handleChunkProgress = (event: any) => {
      if (event.lengthComputable) {
        const chunkProgress =
          Math.round((event.loaded / event.total) * 100) / 100;
        const realProgress = startProgress + deltaProgress * chunkProgress;
        onUploadProgress({ progress: Math.round(realProgress * 100) / 100 });
      }
    };

    const response = await uploadChunk({
      chunk,
      dirId,
      uuid: uid,
      name,
      chunkSize,
      chunkNumber,
      totalSize: size,
      onUploadProgress: handleChunkProgress
    });
    chunkNumber++;

    if (end < size) {
      console.log('Call progress and send next chunk', endProgress);
      start = end;
      onUploadProgress({ progress: endProgress });
      return loop();
    } else {
      console.log('Upload finished');
      return response;
    }
  }

  return loop();
}

function uploadSingle({
  file,
  dirId,
  onUploadProgress,
  cancelToken
}: any): Promise<File> {
  const onProgress = (event: any) => {
    if (event.lengthComputable) {
      const progress = Math.round((event.loaded / event.total) * 100) / 100;
      onUploadProgress({ progress });
    }
  };

  const formData = new FormData();
  formData.append('file', file);

  if (dirId) {
    formData.append('dir_id', dirId);
  }

  return http.post('api/file/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onProgress,
    cancelToken
  });
}

export function upload({ file, dirId, onUploadProgress, cancelToken }: any) {
  if (file.size > CHUNK_SIZE) {
    return uploadChunked({ file, dirId, onUploadProgress });
  }

  return uploadSingle({
    file,
    dirId,
    onUploadProgress,
    cancelToken
  });
}

export async function download(file: File) {
  const response: any = await http.get(`api/file/download/${file.id}`);

  if (response.url) {
    await fileDownload({
      url: response.url
    });
  }
}

export function fetch() {
  return http.get('api/file/');
}

export function read(file: File) {
  return http.get(`api/file/${file.id}`);
}

export function getThumb(file: File) {
  return http.get(`api/file/thumb/${file.id}`);
}

export function getPreview(file: File) {
  return http.get(`api/file/preview/${file.id}`);
}

export function updateFile(file: File) {
  return http.put(`api/file/${file.id}`, file);
}

export function moveFile({ file, dirId }: MoveFilePayload) {
  return http.put(`api/file/move/${file.id}`, {
    directory_id: dirId > 0 ? dirId : null
  });
}

export function deleteFile(file: File) {
  return http.delete(`api/file/${file.id}`);
}

export function shareFile(file: File) {
  return http.post(`api/share/file/${file.id}`);
}
